"use client";

import type { TierId } from "@mw/shared/pricing";

import React, { useState } from "react";
import { clsx } from "clsx";

import { CornerMarkerDot } from "../core/CornerMarkerDot";
import { DottedBorder } from "../core/GradientBorder";
import { LinkTracked } from "../core/LinkTracked";
import { Row } from "./offers/shared";
import type { PricingTableColumn, PricingTableSection } from "./PricingComparisonTable";
import { PricingTableBody } from "./PricingComparisonTable";
import type { ColumnCount } from "./PricingTableV3Shared";
import { PricingValueRenderer } from "./PricingValueRenderer";
import { EventName } from "@mw/shared/analyticsEventNames";
import {
  pricingTableGrid,
  TierHeader,
  MobileTierSelector,
  type TranslatedTierInfo,
  type TranslatedSection,
  type BillingPeriod,
} from "./pricing-table-shared";

// Re-export shared types for consumers
export type { TranslatedTierInfo, TranslatedSection, BillingPeriod } from "./pricing-table-shared";

interface ApiPricingTableClientProps {
  className?: string;
  id?: string;
  tiers: TranslatedTierInfo[];
  sections: TranslatedSection[];
  billingPeriod?: BillingPeriod;
}

/** Product section header with title, description, and CTA */
const ProductSectionHeader = ({
  title,
  description,
  ctaLabel,
  ctaHref,
  sectionId,
  showTopBorder = true,
}: {
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref: string;
  sectionId: string;
  showTopBorder?: boolean;
}) => (
  <div
    className={clsx(
      "tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-justify-between tw-gap-4 tw-px-4 lg:tw-px-10 tw-pt-8 lg:tw-pt-12 tw-pb-4 lg:tw-pb-6",
      showTopBorder && "tw-border-t tw-border-black/5",
    )}
  >
    <div>
      <h3 className='tw-type-2xl tw-mb-2'>{title}</h3>
      <p className='tw-text-gray-600 tw-type-xs'>{description}</p>
    </div>
    <LinkTracked
      eventName={EventName.mw_api_pricing_cta_clicked}
      eventProperties={{ section: sectionId }}
      href={ctaHref}
      asButton
      buttonVariants={{ variant: "elevated", size: "md" }}
    >
      {ctaLabel}
    </LinkTracked>
  </div>
);

/** Single API product section with comparison table */
const ApiProductSection = ({
  section,
  tiers,
  mobileSelectedTierId,
  isFirst,
}: {
  section: TranslatedSection;
  tiers: TranslatedTierInfo[];
  mobileSelectedTierId?: TierId;
  isFirst?: boolean;
}) => {
  const columns: PricingTableColumn<TierId>[] = tiers.map(tier => ({
    id: tier.id,
    mobileLabel: tier.title,
    header: <Row styleVariant='v3' text={tier.title} align='end' />,
  }));

  const columnCount = columns.length as ColumnCount;

  const tableSections: PricingTableSection[] = section.subSections.map(subSection => ({
    label: subSection.label,
    rows: subSection.rows.map((row, rowIndex) => {
      const isLastRow = rowIndex === subSection.rows.length - 1;

      const labelCell = (
        <>
          <Row key={`label-${rowIndex}`} styleVariant='v3' title={row.label} tooltip={row.tooltip} withPadding />
          {!isLastRow && <DottedBorder side='inline' />}
        </>
      );

      const valueCells = row.values.map(valueInfo => {
        const isUnavailable = valueInfo.value.type === "unavailable";
        return (
          <React.Fragment key={valueInfo.tierId}>
            <Row
              styleVariant='v3'
              align='end'
              text={isUnavailable ? undefined : <PricingValueRenderer value={valueInfo.value} />}
              feature={isUnavailable ? false : undefined}
              withPadding
            />
            {!isLastRow && <DottedBorder side='inline' />}
          </React.Fragment>
        );
      });

      return { labelCell, valueCells };
    }),
  }));

  const hasTopSubsectionHeader = !!section.subSections[0]?.label;

  // First section doesn't need top border since sticky header has bottom border
  const showTopBorder = !isFirst;

  return (
    <div>
      <ProductSectionHeader
        title={section.title ?? ""}
        description={section.description ?? ""}
        ctaLabel={section.ctaLabel ?? ""}
        ctaHref={section.ctaHref ?? ""}
        sectionId={section.id}
        showTopBorder={showTopBorder}
      />

      <div className='tw-border-t tw-relative tw-border-black/5'>
        {/* Corner markers - skip top if subsection header exists */}
        {!hasTopSubsectionHeader && <CornerMarkerDot position='topLeft' />}
        {!hasTopSubsectionHeader && <CornerMarkerDot position='topRight' />}
        <CornerMarkerDot position='bottomLeft' />
        <CornerMarkerDot position='bottomRight' />

        {/* Column dividers */}
        <div className='tw-absolute tw-inset-0 tw-pointer-events-none tw-z-10' aria-hidden='true'>
          <div className={clsx("tw-h-full", pricingTableGrid({ columnCount }))}>
            <div />
            {columns.map((column, index) => (
              <div
                key={column.id}
                className={clsx("tw-border-l tw-relative tw-border-black/5", index > 0 && "max-md:tw-hidden")}
              >
                {!hasTopSubsectionHeader && <CornerMarkerDot position='topLeft' />}
                <CornerMarkerDot position='bottomLeft' />
              </div>
            ))}
          </div>
        </div>

        <PricingTableBody
          columns={columns}
          sections={tableSections}
          mobileSelectedColumnId={mobileSelectedTierId}
          gridClassName={pricingTableGrid({ columnCount })}
          labelColumnClassName='tw-pl-4 lg:tw-pl-10 tw-pr-2 lg:tw-pr-4'
          valueColumnClassName='tw-px-1 lg:tw-px-3'
        />
      </div>
    </div>
  );
};

/**
 * Client component for API Pricing Table.
 * Renders pricing comparison tables with sticky tier header.
 */
export const ApiPricingTableClient = ({
  className,
  id,
  tiers,
  sections,
  billingPeriod = "monthly",
}: ApiPricingTableClientProps) => {
  const [selectedMobileTierId, setSelectedMobileTierId] = useState<TierId>(tiers[0]?.id ?? "free");

  if (tiers.length === 0) return null;

  return (
    <div id={id} className={clsx("tw-w-full", className)}>
      <MobileTierSelector
        tiers={tiers}
        selectedTierId={selectedMobileTierId}
        onTierChange={setSelectedMobileTierId}
        billingPeriod={billingPeriod}
      />
      <TierHeader tiers={tiers} billingPeriod={billingPeriod} />
      {sections.map((section, index) => (
        <ApiProductSection
          key={section.id}
          section={section}
          tiers={tiers}
          mobileSelectedTierId={selectedMobileTierId}
          isFirst={index === 0}
        />
      ))}
    </div>
  );
};

export default ApiPricingTableClient;
